/**
 * Interface text for the v1 pages (Product summary, Installs, Install funnel, Engagement, Device retention, Reliability,
 * Diagnostics), their shared components and the v1 page report (CSV and PDF). Keys are prefixed "v1.".
 */
export const en = {
  // Date range and comparison
  'v1.range.label': 'Date range',
  'v1.range.today': 'Today',
  'v1.range.yesterday': 'Yesterday',
  'v1.range.7d': 'Last 7 days',
  'v1.range.30d': 'Last 30 days',
  'v1.range.custom': 'Custom',
  'v1.range.span': '{start} - {end}',
  'v1.range.from': 'From',
  'v1.range.to': 'To',
  'v1.range.startDate': 'Start date',
  'v1.range.endDate': 'End date',
  'v1.range.apply': 'Apply',
  'v1.compare.previous': 'vs previous period',
  'v1.compare.week': 'vs same days last week',
  'v1.provisional.today': 'today',
  'v1.provisional.yesterday': 'yesterday',
  'v1.provisional.and': 'and',
  'v1.provisional.in_progress': '{dates} is still being collected.',
  'v1.provisional.daily_export_pending': 'The daily export for {dates} has not been written yet.',
  'v1.provisional.daily_export_missing': 'The daily export for {dates} never arrived, so only streaming data is available.',
  'v1.provisional.adjacent_table': '{dates} is complete, but its first hours are stored in the export for {tables}, which is not final.',

  // Filters
  'v1.filters.label': 'Data filters',
  'v1.filters.devices': 'Devices',
  'v1.filters.exclInternal': 'Excl. internal',
  'v1.filters.exclInternalTitle': 'Excludes devices classified as internal or test: installed outside Google Play, internal test builds, GA4 debug mode, or listed in the test-device registry. Team phones installed from Google Play are not recognised until they are registered.',
  'v1.filters.allDevices': 'All devices',
  'v1.filters.allDevicesTitle': 'Includes internal and test devices',
  'v1.filters.account': 'Account',
  'v1.filters.all': 'All',
  'v1.filters.guests': 'Guests',
  'v1.filters.registered': 'Registered',
  'v1.filters.compare': 'Compare',
  'v1.filters.off': 'Off',
  'v1.filters.previous': 'Previous period',
  'v1.filters.week': 'Last week',

  // Data states
  'v1.state.aggregateUnavailable': 'Aggregate source unavailable',
  'v1.state.noData': 'No data for this period',
  'v1.state.noDataText': 'The request succeeded, but the selected range has no aggregate data.',
  'v1.state.provisionalTitle': 'Provisional data',
  'v1.state.provisionalOne': 'Provisional: {dates} is not final. Dashed lines can still change until the daily export lands.',
  'v1.state.provisionalMany': 'Provisional: {dates} are not final. Dashed lines can still change until the daily export lands.',
  'v1.state.partialTitle': 'Partial data',
  'v1.state.dayIncomplete': 'The current reporting day may still be incomplete.',
  'v1.state.unavailableSources': 'Unavailable sources: {sources}. Missing metrics remain N/A.',

  // Metric card
  'v1.metric.noData': 'No data',
  'v1.metric.change': 'Change {compare}: {delta}',
  'v1.metric.estimatedUnavailable': 'Estimated · unavailable',
  'v1.metric.mock': 'Mock aggregate',
  'v1.bar.devices': 'devices',
  'v1.funnel.dropOff': '{value} drop-off',
  'v1.funnel.ofStart': '{value} of {start}',

  // Overview (Product summary)
  'v1.overview.title': 'Overview',
  'v1.overview.description': 'A concise view of growth, engagement, product adoption and operating cost.',
  'v1.overview.inclInternal': '{count} incl. internal',
  'v1.overview.activeUsers': 'Active users',
  'v1.overview.activeUsersSub': 'Daily active users',
  'v1.overview.receiptUsage': 'Receipt usage',
  'v1.overview.receiptUsageSub': 'Successful receipt scans per day',
  'v1.overview.featureAdoption': 'Feature adoption',
  'v1.overview.featureAdoptionSub': 'Unique users by core feature',
  'v1.overview.costTrend': 'Cost trend',
  'v1.overview.costMock': 'Placeholder estimate - not a billing statement',
  'v1.overview.costApi': 'Cloud Billing actual cost when the billing source is available',
  'v1.kpi.registeredUsers': 'Registered users',
  'v1.kpi.verifiedUsers': 'Verified users',
  'v1.kpi.newRegistrations': 'New registrations',
  'v1.kpi.newUsers': 'New installs',
  'v1.kpi.activeDevices': 'Active devices',
  'v1.kpi.uninstalls': 'Uninstall signals',
  'v1.kpi.dau': 'Average DAU',
  'v1.kpi.wau': 'WAU',
  'v1.kpi.mau': 'MAU',
  'v1.kpi.receiptsScanned': 'Receipts scanned',
  'v1.kpi.receiptUsers': 'Receipt users',
  'v1.kpi.receiptsPerReceiptUser': 'Receipts per receipt user',
  'v1.kpi.priceSearches': 'Price searches',
  'v1.kpi.priceComparisonUsers': 'Price comparison users',
  'v1.kpi.pantryUsers': 'Pantry users',
  'v1.kpi.budgetUsers': 'Budget users',
  'v1.kpi.shoppingListUsers': 'Shopping list users',
  'v1.kpi.crashFreeUsers': 'Crash-free users',
  'v1.kpi.crashFreeSessions': 'Crash-free sessions',
  'v1.accounts.receiptHelper': 'Fiscal QR receipts imported by production accounts. Image receipts are not counted yet.',
  'v1.accounts.reconstructed': 'Earlier history is reconstructed from accounts that still exist today',
  'v1.accounts.verifiedFrom': 'Verified production accounts · history available from {date}',
  'v1.accounts.verifiedNone': 'Verified production accounts · no observed history yet',
  'v1.accounts.registered': 'Existing production accounts at end of selected period',
  'v1.accounts.created': 'Production accounts created during selected period',

  // Acquisition
  'v1.acq.title': 'Acquisition',
  'v1.acq.description': 'Where new installs come from, which devices they run on, and how many internal devices are filtered out.',
  'v1.acq.newInstalls': 'New installs',
  'v1.acq.newInstallsHelp': 'Unique devices with first_open in this range',
  'v1.acq.activeDevices': 'Active devices',
  'v1.acq.uninstallsThese': 'Uninstall signals, these installs',
  'v1.acq.uninstallsTheseHelp': 'app_remove from devices installed in this range',
  'v1.acq.withoutUninstall': 'Installs without an uninstall signal',
  'v1.acq.withoutUninstallHelp': 'New installs minus the line above',
  'v1.acq.uninstallsAny': 'Uninstall signals, any install date',
  'v1.acq.uninstallsAnyHelp': 'All devices with app_remove in this range; most were installed earlier',
  'v1.acq.rawInstalls': 'New installs (all devices)',
  'v1.acq.rawInstallsHelp': 'Includes internal and test devices',
  'v1.acq.excluded': 'Internal installs excluded',
  'v1.acq.excludedHelp': 'Removed by the Excl. internal view',
  'v1.acq.activeSplit': '{fresh} installed in this range, {earlier} earlier',
  'v1.acq.installsChartFiltered': 'Excluding internal devices, with all devices for reference',
  'v1.acq.installsChartAll': 'All devices',
  'v1.acq.activeChartSub': 'Devices with foreground engagement per day',
  'v1.acq.uninstallsChart': 'Uninstall signals',
  'v1.acq.uninstallsChartSub': 'Devices that sent app_remove per day',
  'v1.acq.breakdowns': 'Breakdowns',
  'v1.acq.breakdownsText': 'New installs, active devices and uninstall signals by dimension. Shares are of new installs.',
  'v1.acq.dimension': 'Breakdown dimension',
  'v1.acq.share': 'Share',
  'v1.acq.noData': 'No data',
  'v1.acq.noDevices': 'No devices in this range for this dimension.',
  'v1.acq.attribution': 'Attribution',
  'v1.acq.status': 'Status:',
  'v1.acq.status.available': 'available',
  'v1.acq.status.partial': 'partial',
  'v1.acq.status.unavailable': 'unavailable',
  'v1.acq.firstTouch': 'First-touch source / medium',
  'v1.acq.playConsole': 'Google Play Console: not connected',
  'v1.acq.internal': 'Internal and test devices',
  'v1.acq.exclusions': 'Device exclusions',
  'v1.acq.internalFiltered': 'Excl. internal view: these devices are removed from every GA4 figure.',
  'v1.acq.internalAll': 'All devices view: these devices are included in every GA4 figure.',
  'v1.acq.devicesSeen': 'Devices seen',
  'v1.acq.classified': 'Classified internal',
  'v1.acq.registry': 'Test-device registry',
  'v1.acq.registry.applied': 'Applied',
  'v1.acq.registry.unavailable': 'Configured but unreadable',
  'v1.acq.registry.not_configured': 'Not configured',
  'v1.acq.rule': 'Rule',
  'v1.acq.devices': 'Devices',
  'v1.acq.rulesFooter': 'A device can match more than one rule, so the rule counts can add up to more than the classified total. The raw New installs card always shows the unfiltered count.',

  // Activation
  'v1.act.title': 'Activation',
  'v1.act.description': 'How new installs, guests and registered users alike reach their first meaningful action. Registration is a separate step and is shown on its own below.',
  'v1.act.segment.all': 'All installs',
  'v1.act.segment.guest': 'Guests',
  'v1.act.segment.registered': 'Registered',
  'v1.act.segment.unknown': 'No account type yet',
  'v1.act.installToActivation': 'Install to activation',
  'v1.act.installToActivationText': 'Devices installed in the selected range, guests and registered users together. Opening a feature is not activation; a first meaningful action is.',
  'v1.act.accountType': 'Account type',
  'v1.act.installs': 'installs',
  'v1.act.funnelLabel': 'Install funnel, {segment}',
  'v1.act.firstAction': 'First meaningful action',
  'v1.act.firstActionSub': 'Which action came first, among all installs that took one',
  'v1.act.rates': 'Activation rates',
  'v1.act.within24h': 'Activated within 24 hours',
  'v1.act.within7d': 'Activated within 7 days',
  'v1.act.returned': 'Came back on a later day',
  'v1.act.firstScreen': 'Time to first screen, median',
  'v1.act.firstActionMedian': 'Time to first action, median',
  'v1.act.firstActionP90': 'Time to first action, p90',
  'v1.act.windowOldEnough': '{activated} of {eligible} installs old enough',
  'v1.act.windowNextDay': '{activated} of {eligible} installs with a final next day',
  'v1.act.core': 'Core feature activation',
  'v1.act.coreText': 'Share of {segment} that did each thing at least once after installing.',
  'v1.act.coreSegment.all': 'all installs',
  'v1.act.coreSegment.guest': 'guests',
  'v1.act.coreSegment.registered': 'registered users',
  'v1.act.coreSegment.unknown': 'installs with no account type yet',
  'v1.act.devicesCount': '{count} devices',
  'v1.act.registration': 'Registration',
  'v1.act.registrationText': 'Sign-up only, in the selected range. This is separate from product activation: it says nothing about what people do in the app.',
  'v1.act.signUpsStarted': 'sign-ups started',
  'v1.act.registrationFunnel': 'Registration funnel',
  'v1.act.journeys': 'Journey funnels',
  'v1.act.journeysText': 'Devices active in the range that reached each step, cumulatively.',
  'v1.act.firstStep': 'first step',
  'v1.act.journeyFunnel': '{name} funnel',
  'v1.act.events': 'Event availability',
  'v1.act.eventsText': 'The first day each event the funnels rely on appears in the data. A zero before that day means the app could not report the event yet, not that nobody did it.',
  'v1.act.event': 'Event',
  'v1.act.firstSeen': 'First seen',
  'v1.act.devicesAllTime': 'Devices, all time',
  'v1.act.neverRecorded': 'Never recorded',

  // Engagement
  'v1.eng.title': 'Engagement',
  'v1.eng.description': "Adoption, frequency and repeat usage across Aruha's core workflows.",
  'v1.eng.denominator': 'Denominator for every adoption share below',
  'v1.eng.featureUsage': 'Feature usage',
  'v1.eng.feature': 'Feature',
  'v1.eng.uniqueUsers': 'Unique users',
  'v1.eng.shareActive': '% of active devices',
  'v1.eng.events': 'Events',
  'v1.eng.eventsPerUser': 'Events / user',
  'v1.eng.repeatUsers': 'Repeat users',
  'v1.eng.repeatRate': 'Repeat rate',
  'v1.eng.footer': 'Repeat users used the feature on two or more different days in the range. A feature with no events shows 0 here: the export was read and the feature was not used.',
  'v1.eng.daily': 'Daily feature usage',
  'v1.eng.dailySub': 'Events by feature and day',
  'v1.eng.journeys': 'Main journeys',
  'v1.eng.journeysText': 'Devices that reached each step in the range, cumulatively.',

  // Retention
  'v1.ret.title': 'Retention',
  'v1.ret.description': 'Cohort return rates from first install, without exposing individual user activity.',
  'v1.ret.matured': '{count} devices in matured cohorts',
  'v1.ret.d1': 'D1 retention',
  'v1.ret.d7': 'D7 retention',
  'v1.ret.d30': 'D30 retention',
  'v1.ret.dayTitle': 'Share of installs still active on day {day} after installing',
  'v1.ret.cohorts': 'Retention cohorts',
  'v1.ret.cohortsText': 'Share of each install cohort that was active on the indicated day. Darker cells retained more.',
  'v1.ret.cohort': 'Cohort',
  'v1.ret.size': 'Size',
  'v1.ret.status': 'Status',
  'v1.ret.uninstallSignals': 'Uninstall signals',
  'v1.ret.medianUninstall': 'Median {hours} h after install',
  'v1.ret.footer': 'A dash means the cohort has not matured yet; it is not interpreted as 0%.',
  'v1.ret.activity': 'Activity',
  'v1.ret.activityText': 'Who is coming back, how often, and who has gone quiet.',
  'v1.ret.activeDevices': 'Active devices',
  'v1.ret.newDevices': 'New devices',
  'v1.ret.newDevicesHelp': 'First opened in this range',
  'v1.ret.returningDevices': 'Returning devices',
  'v1.ret.returningDevicesHelp': 'Active, installed before the range',
  'v1.ret.stickiness': 'Stickiness',
  'v1.ret.stickinessHelp': 'Average daily actives as a share of MAU',
  'v1.ret.frequency': 'Engagement frequency',
  'v1.ret.frequencySub': 'Devices by number of active days in the range',
  'v1.ret.recency': 'Recency',
  'v1.ret.recencySub': 'Devices by days since they were last active',
  'v1.ret.dailyActive': 'Daily active devices',
  'v1.ret.quiet': 'Gone quiet',
  'v1.ret.quietSub': 'Devices active in the prior 30 days but not in the last 7',
  'v1.ret.uninstallDevices': 'Devices with an uninstall signal',
  'v1.ret.uninstallInstalls': 'Installs in these cohorts',
  'v1.ret.signalRate': 'Signal rate',
  'v1.ret.notMatured': '{label} not matured',
  'v1.ret.notMaturedTitle': 'Not matured',
  'v1.ret.retainedOf': '{retained} of {size} devices',
  'v1.ret.maturedBadge': 'Matured',
  'v1.ret.maturing': 'Maturing',

  // Reliability
  'v1.rel.title': 'Reliability',
  'v1.rel.description': 'Crash-free rates, crashes, startup time and latency from Firebase and the backend. A missing export says why.',
  'v1.rel.exports': 'Firebase exports',
  'v1.rel.ready': 'Ready',
  'v1.rel.readySince': 'Ready, since {date}',
  'v1.rel.crashFreeUsers': 'Crash-free users',
  'v1.rel.crashFreeSessions': 'Crash-free sessions',
  'v1.rel.crashCount': 'Crash count',
  'v1.rel.nonFatal': 'Non-fatal errors',
  'v1.rel.sessions': 'Sessions',
  'v1.rel.crashedSessions': 'Crashed sessions',
  'v1.rel.startP50': 'App start p50',
  'v1.rel.startP95': 'App start p95',
  'v1.rel.receiptFailure': 'Receipt failure rate',
  'v1.rel.searchFailure': 'Price search failure rate',
  'v1.rel.receiptAvg': 'Receipt avg latency',
  'v1.rel.receiptP95': 'Receipt p95 latency',
  'v1.rel.searchAvg': 'Price search avg latency',
  'v1.rel.searchP95': 'Price search p95 latency',
  'v1.rel.errors': 'Errors over time',
  'v1.rel.p95': 'p95 latency',
  'v1.rel.traces': 'Firebase Performance traces',
  'v1.rel.tracesText': 'The Analytics v1 custom traces; unavailable exports remain N/A.',
  'v1.rel.trace': 'Trace',
  'v1.rel.count': 'Count',
  'v1.rel.byVersion': 'Crashes by app version',
  'v1.rel.byVersionShort': 'Crashes by version',
  'v1.rel.byVersionText': 'Fatal crashes and ANRs. Sessions come from the Firebase Sessions export.',
  'v1.rel.version': 'Version',
  'v1.rel.build': 'Build',
  'v1.rel.crashes': 'Crashes',
  'v1.rel.noCrashes': 'No crashes recorded in this range.',
  'v1.rel.topIssues': 'Top crash issues',
  'v1.rel.topIssuesText': 'Grouped by Crashlytics issue; counts only, no user data.',
  'v1.rel.issue': 'Issue',
  'v1.rel.type': 'Type',

  // Diagnostics (temporary admin page)
  'v1.diag.title': 'Diagnostics',
  'v1.diag.description': 'Temporary rollout check. Calls all nine analytics endpoints on the revision under test with your signed-in session. Shows statuses and source readiness only, never credentials or data.',
  'v1.diag.target': 'Target',
  'v1.diag.running': 'Running...',
  'v1.diag.run': 'Run checks',
  'v1.diag.copied': 'Copied',
  'v1.diag.copy': 'Copy summary',
  'v1.diag.pass': 'Rollout gate: pass',
  'v1.diag.fail': 'Rollout gate: fail',
  'v1.diag.passText': 'All nine endpoints answered 200 with data and every expected source was ready.',
  'v1.diag.endpoints': 'Endpoints',
  'v1.diag.rangeText': '{start} to {end}',
  'v1.diag.endpoint': 'Endpoint',
  'v1.diag.http': 'HTTP',
  'v1.diag.time': 'Time',
  'v1.diag.completeness': 'Completeness',
  'v1.diag.data': 'Data',
  'v1.diag.sources': 'Sources',
  'v1.diag.failed': 'failed',
  'v1.diag.notReady': 'Sources not ready',
  'v1.diag.source': 'Source',
  'v1.diag.status': 'Status',
  'v1.diag.reason': 'Reason',
  'v1.diag.seenOn': 'Seen on',

  // Page report (CSV and PDF)
  'v1.export.button': 'Export',
  'v1.export.preparing': 'Preparing…',
  'v1.export.csvCurrent': 'Current section',
  'v1.export.pdfCurrent': 'PDF current view',
  'v1.export.pdfCurrentSub': 'Print-friendly report',
  'v1.export.pdfFull': 'Full PDF report',
  'v1.export.pdfFullSub': 'All {count} sections',
  'v1.export.failed': 'Export failed.',
  'v1.section.overview': 'Overview',
  'v1.section.acquisition': 'Acquisition',
  'v1.section.activation': 'Activation',
  'v1.section.retention': 'Retention',
  'v1.section.features': 'Engagement',
  'v1.section.receipts': 'Receipts',
  'v1.section.price-comparison': 'Price Comparison',
  'v1.section.costs': 'Costs',
  'v1.section.reliability': 'Reliability',
  'v1.report.title': 'Analytics report',
  'v1.report.generated': 'Generated',
  'v1.report.source': 'Source',
  'v1.report.coverage': '{source} coverage',
  'v1.report.notCollected': 'N/A · not collected',
  'v1.report.from': 'from {date}',
  'v1.report.startUnknown': 'start unknown',
  'v1.report.completeThrough': 'complete through {date}',
  'v1.report.completeUnknown': 'complete-through unknown',
  'v1.report.empty': 'No aggregate data is available for this date range.',
  'v1.report.estimatedUnavailable': 'ESTIMATED · UNAVAILABLE',
  'v1.report.unavailable': 'UNAVAILABLE',
  'v1.report.kind.actual': 'ACTUAL',
  'v1.report.kind.estimated': 'ESTIMATED',
  'v1.report.kind.mock': 'MOCK',
  'v1.report.fullCover': 'Full aggregate report · {start} - {end}',
  'v1.report.footer': 'Aggregate data only · No personal or individual-level data',
  'v1.report.mockSource': 'MOCK DATA - NOT PRODUCTION',
  'v1.report.actualEstimated': 'ACTUAL + ESTIMATED',
  'v1.report.costProvenance': 'Measurement provenance shown in source metadata',
  'v1.report.installsByVersion': 'Installs by app version',
  'v1.report.installFunnel': 'Install funnel',
  'v1.report.step': 'Step',
  'v1.report.devices': 'Devices',
  'v1.report.fromPrevious': 'From previous',
  'v1.report.fromStart': 'From start',
  'v1.report.dropOff': 'Drop-off',
  'v1.report.activeShare': 'Active share',
  'v1.report.estimatedWarning': 'Cost values marked ESTIMATED are modelled values and are not an invoice.',
  'v1.report.dailyCost': 'Daily cost',
  'v1.report.byService': 'Cost by service',
  'v1.report.service': 'Service',
  'v1.report.cost': 'Cost',
  'v1.report.measurement': 'Measurement',
  'v1.report.imagePerDay': 'Image receipts per day',
  'v1.report.imageOutcomes': 'Image processing outcomes',
  'v1.report.retailerDistribution': 'Retailer distribution',
  'v1.report.processingLatency': 'Processing latency',
  'v1.report.retailers': 'Retailers',
  'v1.report.retailer': 'Retailer',
  'v1.report.receipts': 'Receipts',
  'v1.report.searchesPerDay': 'Searches per day',
  'v1.report.searchOutcomes': 'Search outcomes',
  'v1.report.latency': 'Latency',
  'v1.csv.report': 'Report',
  'v1.csv.section': 'Section',
  'v1.csv.dateRange': 'Date range',
  'v1.csv.rangeValue': '{start} to {end}',
  'v1.csv.timezone': 'Timezone',
  'v1.csv.generatedAt': 'Generated at',
  'v1.csv.dataSource': 'Data source',
  'v1.csv.sourceCoverage': 'Source coverage',
  'v1.csv.privacy': 'Privacy',
  'v1.csv.privacyValue': 'Aggregate data only',
  'v1.csv.notCollected': 'N/A - not collected',
  'v1.csv.kpi': 'KPI',
  'v1.csv.trend': 'Trend',
  'v1.csv.status': 'Status',
  'v1.csv.dataset': 'Dataset',
  'v1.csv.featureAdoption': 'Feature adoption',
  'v1.csv.uniqueUsers': 'Unique users',
  'v1.csv.breakdown': 'Breakdown',
  'v1.csv.breakdownInstalls': '{dimension}: new installs',
  'v1.csv.breakdownActive': '{dimension}: active devices',
  'v1.csv.exclusionRule': 'Exclusion rule',
  'v1.csv.installFunnel': 'Install funnel, {segment}',
  'v1.csv.devices': 'Devices',
  'v1.csv.users': 'Users',
  'v1.csv.eligible': '{window} eligible',
  'v1.csv.rate': '{window} rate',
  'v1.csv.registrationFunnel': 'Registration funnel',
  'v1.csv.conversionPrevious': 'Conversion from previous',
  'v1.csv.conversionStart': 'Conversion from start',
  'v1.csv.dropOffPrevious': 'Drop-off from previous',
  'v1.csv.cohort': 'Cohort',
  'v1.csv.cohortSize': 'Cohort size',
  'v1.csv.periodRetention': '{period} retention',
  'v1.csv.activity': 'Activity',
  'v1.csv.uninstallSignals': 'Uninstall signals',
  'v1.csv.feature': 'Feature',
  'v1.csv.activeShare': 'Active user share',
  'v1.csv.eventCount': 'Event count',
  'v1.csv.eventsPerUser': 'Events per user',
  'v1.csv.repeatUsers': 'Repeat users',
  'v1.csv.repeatRate': 'Repeat rate',
  'v1.csv.retailerDistribution': 'Retailer distribution',
  'v1.csv.receipts': 'Receipts',
  'v1.csv.share': 'Share',
  'v1.csv.costByService': 'Cost by service',
  'v1.csv.cost': 'Cost',
  'v1.csv.performanceTrace': 'Performance trace',
  'v1.csv.traceCount': '{trace} count',
  'v1.csv.traceP50': '{trace} p50',
  'v1.csv.traceP95': '{trace} p95',

  // Report metric labels that the pages do not show (CSV and PDF only)
  'v1.label.activeFromNewInstalls': 'Active devices installed in range',
  'v1.label.activeFromEarlierInstalls': 'Active devices installed earlier',
  'v1.label.rawUninstalls': 'Uninstall signals (all devices)',
  'v1.label.uninstallsAny': 'Uninstall signals (any install date)',
  'v1.label.uninstallsThese': 'Uninstall signals (these installs)',
  'v1.label.processed': 'Image receipts processed',
  'v1.label.successful': 'Image processing successful',
  'v1.label.failed': 'Image processing failed',
  'v1.label.duplicates': 'Duplicates',
  'v1.label.successRate': 'Image processing success rate',
  'v1.label.uniqueReceiptUsers': 'Unique receipt users',
  'v1.label.uniqueUsers': 'Unique users',
  'v1.label.receiptsPerUser': 'Receipts per user',
  'v1.label.receiptsPerUserShort': 'Receipts / user',
  'v1.label.avgItems': 'Average items per receipt',
  'v1.label.itemsPerReceipt': 'Items / receipt',
  'v1.label.avgProcessing': 'Average processing duration',
  'v1.label.avgProcessingShort': 'Average processing',
  'v1.label.processingP50': 'Processing latency p50',
  'v1.label.processingP95': 'Processing latency p95',
  'v1.label.latencyP50': 'Latency p50',
  'v1.label.latencyP95': 'Latency p95',
  'v1.label.cacheHitRate': 'Cache hit rate',
  'v1.label.enrichment': 'Enrichment operations',
  'v1.label.totalSearches': 'Total searches',
  'v1.label.searchesPerUser': 'Searches per user',
  'v1.label.searchesPerUserShort': 'Searches / user',
  'v1.label.successfulSearches': 'Successful searches',
  'v1.label.successfulShort': 'Successful',
  'v1.label.noResultSearches': 'No-result searches',
  'v1.label.noResultShort': 'No result',
  'v1.label.failures': 'Failures',
  'v1.label.noResultRate': 'No-result rate',
  'v1.label.avgResults': 'Average results returned',
  'v1.label.resultsReturned': 'Results returned',
  'v1.label.avgChainCoverage': 'Average max chain coverage',
  'v1.label.maxChainCoverage': 'Max chain coverage',
  'v1.label.avgLatency': 'Average latency',
  'v1.label.browseRequests': 'Browse requests',
  'v1.label.browsePages': 'Browse pages loaded',
  'v1.label.pagesLoaded': 'Pages loaded',
  'v1.label.avgProductsPerBrowse': 'Average products per browse',
  'v1.label.productsPerBrowse': 'Products / browse',
  'v1.label.loadMore': 'Load-more usage',
  'v1.label.totalCost': 'Total GCP cost',
  'v1.label.costPerDay': 'Average cost per day',
  'v1.label.costPerDayShort': 'Cost / day',
  'v1.label.aiCost': 'AI cost',
  'v1.label.costPerActiveUser': 'Cost per active user',
  'v1.label.costPerActiveUserShort': 'Cost / active user',
  'v1.label.costPerReceipt': 'Cost per image receipt',
  'v1.label.costPerReceiptShort': 'Cost / image receipt',
  'v1.label.costPerSearch': 'Cost per price search',
  'v1.label.costPerSearchShort': 'Cost / search',
  'v1.label.aiCallsPerReceipt': 'AI calls per image receipt',
  'v1.label.aiCallsPerReceiptShort': 'AI calls / image receipt',
  'v1.label.aiCalls': 'AI calls',
  'v1.label.aiCostPerCall': 'AI cost per call',
  'v1.label.receiptAvgLatency': 'Receipt average latency',
  'v1.label.receiptLatency': 'Receipt latency',
  'v1.label.receiptP95': 'Receipt p95',
  'v1.label.searchAvgLatency': 'Price search average latency',
  'v1.label.searchLatency': 'Search latency',
  'v1.label.searchP95': 'Search p95',
  'v1.label.searchFailure': 'Search failure rate',
  'v1.label.crashedUsers': 'Crashed users',
};

export const sr: Record<keyof typeof en, string> = {
  'v1.range.label': 'Period',
  'v1.range.today': 'Danas',
  'v1.range.yesterday': 'Juče',
  'v1.range.7d': 'Poslednjih 7 dana',
  'v1.range.30d': 'Poslednjih 30 dana',
  'v1.range.custom': 'Prilagođeno',
  'v1.range.span': '{start} - {end}',
  'v1.range.from': 'Od',
  'v1.range.to': 'Do',
  'v1.range.startDate': 'Početni datum',
  'v1.range.endDate': 'Krajnji datum',
  'v1.range.apply': 'Primeni',
  'v1.compare.previous': 'u odnosu na prethodni period',
  'v1.compare.week': 'u odnosu na iste dane prošle nedelje',
  'v1.provisional.today': 'danas',
  'v1.provisional.yesterday': 'juče',
  'v1.provisional.and': 'i',
  'v1.provisional.in_progress': 'Podaci za {dates} se još prikupljaju.',
  'v1.provisional.daily_export_pending': 'Dnevni izvoz za {dates} još nije upisan.',
  'v1.provisional.daily_export_missing': 'Dnevni izvoz za {dates} nikada nije stigao, pa su dostupni samo podaci pristigli u realnom vremenu.',
  'v1.provisional.adjacent_table': 'Podaci za {dates} su potpuni, ali su prvi sati zapisani u izvozu za {tables}, koji još nije konačan.',

  'v1.filters.label': 'Filteri podataka',
  'v1.filters.devices': 'Uređaji',
  'v1.filters.exclInternal': 'Bez internih',
  'v1.filters.exclInternalTitle': 'Izostavlja uređaje označene kao interne ili test uređaje: instalirane mimo Google Play prodavnice, interne test verzije, GA4 režim za otklanjanje grešaka ili uređaje iz registra test uređaja. Telefoni tima instalirani sa Google Play prodavnice prepoznaju se tek kada se upišu u registar.',
  'v1.filters.allDevices': 'Svi uređaji',
  'v1.filters.allDevicesTitle': 'Uključuje interne i test uređaje',
  'v1.filters.account': 'Nalog',
  'v1.filters.all': 'Svi',
  'v1.filters.guests': 'Gosti',
  'v1.filters.registered': 'Registrovani',
  'v1.filters.compare': 'Poređenje',
  'v1.filters.off': 'Isključeno',
  'v1.filters.previous': 'Prethodni period',
  'v1.filters.week': 'Prošla nedelja',

  'v1.state.aggregateUnavailable': 'Izvor zbirnih podataka nije dostupan',
  'v1.state.noData': 'Nema podataka za ovaj period',
  'v1.state.noDataText': 'Zahtev je uspeo, ali izabrani period nema zbirnih podataka.',
  'v1.state.provisionalTitle': 'Privremeni podaci',
  'v1.state.provisionalOne': 'Privremeno: podaci za {dates} još nisu konačni. Isprekidane linije mogu da se promene dok ne stigne dnevni izvoz.',
  'v1.state.provisionalMany': 'Privremeno: podaci za {dates} još nisu konačni. Isprekidane linije mogu da se promene dok ne stigne dnevni izvoz.',
  'v1.state.partialTitle': 'Delimični podaci',
  'v1.state.dayIncomplete': 'Tekući izveštajni dan možda još nije potpun.',
  'v1.state.unavailableSources': 'Nedostupni izvori: {sources}. Metrike koje nedostaju ostaju N/D.',

  'v1.metric.noData': 'Nema podataka',
  'v1.metric.change': 'Promena {compare}: {delta}',
  'v1.metric.estimatedUnavailable': 'Procena · nije dostupno',
  'v1.metric.mock': 'Probni zbir',
  'v1.bar.devices': 'uređaja',
  'v1.funnel.dropOff': 'odustalo {value}',
  'v1.funnel.ofStart': '{value} od: {start}',

  'v1.overview.title': 'Pregled',
  'v1.overview.description': 'Sažet pregled rasta, angažovanja, korišćenja funkcija i troškova rada.',
  'v1.overview.inclInternal': '{count} sa internim uređajima',
  'v1.overview.activeUsers': 'Aktivni korisnici',
  'v1.overview.activeUsersSub': 'Dnevno aktivni korisnici',
  'v1.overview.receiptUsage': 'Korišćenje računa',
  'v1.overview.receiptUsageSub': 'Uspešna skeniranja računa po danu',
  'v1.overview.featureAdoption': 'Korišćenje funkcija',
  'v1.overview.featureAdoptionSub': 'Jedinstveni korisnici po glavnoj funkciji',
  'v1.overview.costTrend': 'Kretanje troškova',
  'v1.overview.costMock': 'Okvirna procena, nije obračun troškova',
  'v1.overview.costApi': 'Stvarni trošak iz Cloud Billing izvoza kada je izvor dostupan',
  'v1.kpi.registeredUsers': 'Registrovani korisnici',
  'v1.kpi.verifiedUsers': 'Potvrđeni korisnici',
  'v1.kpi.newRegistrations': 'Nove registracije',
  'v1.kpi.newUsers': 'Nove instalacije',
  'v1.kpi.activeDevices': 'Aktivni uređaji',
  'v1.kpi.uninstalls': 'Signali deinstalacije',
  'v1.kpi.dau': 'Prosečan DAU',
  'v1.kpi.wau': 'WAU',
  'v1.kpi.mau': 'MAU',
  'v1.kpi.receiptsScanned': 'Skenirani računi',
  'v1.kpi.receiptUsers': 'Korisnici računa',
  'v1.kpi.receiptsPerReceiptUser': 'Računa po korisniku računa',
  'v1.kpi.priceSearches': 'Pretrage cena',
  'v1.kpi.priceComparisonUsers': 'Korisnici poređenja cena',
  'v1.kpi.pantryUsers': 'Korisnici ostave',
  'v1.kpi.budgetUsers': 'Korisnici budžeta',
  'v1.kpi.shoppingListUsers': 'Korisnici liste za kupovinu',
  'v1.kpi.crashFreeUsers': 'Korisnici bez pada aplikacije',
  'v1.kpi.crashFreeSessions': 'Sesije bez pada aplikacije',
  'v1.accounts.receiptHelper': 'Fiskalni računi uvezeni preko QR koda na produkcijskim nalozima. Računi sa fotografije se još ne broje.',
  'v1.accounts.reconstructed': 'Ranija istorija je rekonstruisana iz naloga koji i danas postoje',
  'v1.accounts.verifiedFrom': 'Potvrđeni produkcijski nalozi · istorija je dostupna od {date}',
  'v1.accounts.verifiedNone': 'Potvrđeni produkcijski nalozi · istorija još nije zabeležena',
  'v1.accounts.registered': 'Produkcijski nalozi koji postoje na kraju izabranog perioda',
  'v1.accounts.created': 'Produkcijski nalozi otvoreni u izabranom periodu',

  'v1.acq.title': 'Pridobijanje',
  'v1.acq.description': 'Odakle dolaze nove instalacije, na kojim uređajima rade i koliko internih uređaja je izostavljeno.',
  'v1.acq.newInstalls': 'Nove instalacije',
  'v1.acq.newInstallsHelp': 'Jedinstveni uređaji sa događajem first_open u ovom periodu',
  'v1.acq.activeDevices': 'Aktivni uređaji',
  'v1.acq.uninstallsThese': 'Signali deinstalacije, ove instalacije',
  'v1.acq.uninstallsTheseHelp': 'app_remove sa uređaja instaliranih u ovom periodu',
  'v1.acq.withoutUninstall': 'Instalacije bez signala deinstalacije',
  'v1.acq.withoutUninstallHelp': 'Nove instalacije umanjene za red iznad',
  'v1.acq.uninstallsAny': 'Signali deinstalacije, bilo koji datum instalacije',
  'v1.acq.uninstallsAnyHelp': 'Svi uređaji sa app_remove u ovom periodu; većina je instalirana ranije',
  'v1.acq.rawInstalls': 'Nove instalacije (svi uređaji)',
  'v1.acq.rawInstallsHelp': 'Uključuje interne i test uređaje',
  'v1.acq.excluded': 'Izostavljene interne instalacije',
  'v1.acq.excludedHelp': 'Uklonjene u prikazu „Bez internih”',
  'v1.acq.activeSplit': '{fresh} instalirano u ovom periodu, {earlier} ranije',
  'v1.acq.installsChartFiltered': 'Bez internih uređaja, uz sve uređaje radi poređenja',
  'v1.acq.installsChartAll': 'Svi uređaji',
  'v1.acq.activeChartSub': 'Uređaji sa aktivnošću u prvom planu po danu',
  'v1.acq.uninstallsChart': 'Signali deinstalacije',
  'v1.acq.uninstallsChartSub': 'Uređaji koji su poslali app_remove po danu',
  'v1.acq.breakdowns': 'Raspodele',
  'v1.acq.breakdownsText': 'Nove instalacije, aktivni uređaji i signali deinstalacije po dimenziji. Udeli se računaju od novih instalacija.',
  'v1.acq.dimension': 'Dimenzija raspodele',
  'v1.acq.share': 'Udeo',
  'v1.acq.noData': 'Nema podataka',
  'v1.acq.noDevices': 'Nema uređaja u ovom periodu za ovu dimenziju.',
  'v1.acq.attribution': 'Atribucija',
  'v1.acq.status': 'Stanje:',
  'v1.acq.status.available': 'dostupno',
  'v1.acq.status.partial': 'delimično',
  'v1.acq.status.unavailable': 'nedostupno',
  'v1.acq.firstTouch': 'Izvor / medijum prvog kontakta',
  'v1.acq.playConsole': 'Google Play Console: nije povezan',
  'v1.acq.internal': 'Interni i test uređaji',
  'v1.acq.exclusions': 'Izostavljeni uređaji',
  'v1.acq.internalFiltered': 'Prikaz „Bez internih”: ovi uređaji su uklonjeni iz svih GA4 brojki.',
  'v1.acq.internalAll': 'Prikaz „Svi uređaji”: ovi uređaji su uključeni u sve GA4 brojke.',
  'v1.acq.devicesSeen': 'Viđeni uređaji',
  'v1.acq.classified': 'Označeni kao interni',
  'v1.acq.registry': 'Registar test uređaja',
  'v1.acq.registry.applied': 'Primenjen',
  'v1.acq.registry.unavailable': 'Podešen, ali nečitljiv',
  'v1.acq.registry.not_configured': 'Nije podešen',
  'v1.acq.rule': 'Pravilo',
  'v1.acq.devices': 'Uređaji',
  'v1.acq.rulesFooter': 'Uređaj može da odgovara za više pravila, pa zbir po pravilima može biti veći od ukupnog broja označenih. Kartica „Nove instalacije (svi uređaji)” uvek prikazuje nefiltriran broj.',

  'v1.act.title': 'Aktivacija',
  'v1.act.description': 'Kako nove instalacije, i gosti i registrovani korisnici, stižu do prve smislene radnje. Registracija je zaseban korak i prikazana je posebno ispod.',
  'v1.act.segment.all': 'Sve instalacije',
  'v1.act.segment.guest': 'Gosti',
  'v1.act.segment.registered': 'Registrovani',
  'v1.act.segment.unknown': 'Još bez vrste naloga',
  'v1.act.installToActivation': 'Od instalacije do aktivacije',
  'v1.act.installToActivationText': 'Uređaji instalirani u izabranom periodu, gosti i registrovani zajedno. Otvaranje funkcije nije aktivacija; prva smislena radnja jeste.',
  'v1.act.accountType': 'Vrsta naloga',
  'v1.act.installs': 'instalacija',
  'v1.act.funnelLabel': 'Levak instalacija, {segment}',
  'v1.act.firstAction': 'Prva smislena radnja',
  'v1.act.firstActionSub': 'Koja radnja je bila prva, među svim instalacijama koje su je izvršile',
  'v1.act.rates': 'Stope aktivacije',
  'v1.act.within24h': 'Aktivirano u roku od 24 sata',
  'v1.act.within7d': 'Aktivirano u roku od 7 dana',
  'v1.act.returned': 'Vratili su se narednih dana',
  'v1.act.firstScreen': 'Vreme do prvog ekrana, medijana',
  'v1.act.firstActionMedian': 'Vreme do prve radnje, medijana',
  'v1.act.firstActionP90': 'Vreme do prve radnje, p90',
  'v1.act.windowOldEnough': '{activated} od {eligible} dovoljno starih instalacija',
  'v1.act.windowNextDay': '{activated} od {eligible} instalacija sa završenim narednim danom',
  'v1.act.core': 'Aktivacija glavnih funkcija',
  'v1.act.coreText': 'Udeo u grupi „{segment}” koji je posle instalacije bar jednom uradio svaku radnju.',
  'v1.act.coreSegment.all': 'sve instalacije',
  'v1.act.coreSegment.guest': 'gosti',
  'v1.act.coreSegment.registered': 'registrovani korisnici',
  'v1.act.coreSegment.unknown': 'instalacije još bez vrste naloga',
  'v1.act.devicesCount': '{count} uređaja',
  'v1.act.registration': 'Registracija',
  'v1.act.registrationText': 'Samo registracija, u izabranom periodu. Odvojeno je od aktivacije u proizvodu: ne govori ništa o tome šta ljudi rade u aplikaciji.',
  'v1.act.signUpsStarted': 'započetih registracija',
  'v1.act.registrationFunnel': 'Levak registracije',
  'v1.act.journeys': 'Levkovi korisničkih putanja',
  'v1.act.journeysText': 'Uređaji aktivni u periodu koji su stigli do svakog koraka, kumulativno.',
  'v1.act.firstStep': 'prvi korak',
  'v1.act.journeyFunnel': 'Levak: {name}',
  'v1.act.events': 'Dostupnost događaja',
  'v1.act.eventsText': 'Prvi dan kada se svaki događaj na koji se levkovi oslanjaju pojavljuje u podacima. Nula pre tog dana znači da aplikacija još nije mogla da prijavi događaj, a ne da ga niko nije uradio.',
  'v1.act.event': 'Događaj',
  'v1.act.firstSeen': 'Prvi put viđen',
  'v1.act.devicesAllTime': 'Uređaji, ukupno',
  'v1.act.neverRecorded': 'Nikad zabeležen',

  'v1.eng.title': 'Angažovanje',
  'v1.eng.description': 'Korišćenje, učestalost i ponovna upotreba glavnih tokova u Aruhi.',
  'v1.eng.denominator': 'Imenilac za svaki udeo korišćenja ispod',
  'v1.eng.featureUsage': 'Korišćenje funkcija',
  'v1.eng.feature': 'Funkcija',
  'v1.eng.uniqueUsers': 'Jedinstveni korisnici',
  'v1.eng.shareActive': '% aktivnih uređaja',
  'v1.eng.events': 'Događaji',
  'v1.eng.eventsPerUser': 'Događaja po korisniku',
  'v1.eng.repeatUsers': 'Povratni korisnici',
  'v1.eng.repeatRate': 'Stopa ponovnog korišćenja',
  'v1.eng.footer': 'Povratni korisnici su koristili funkciju u dva ili više različitih dana u periodu. Funkcija bez događaja ovde ima 0: izvoz je pročitan, a funkcija nije korišćena.',
  'v1.eng.daily': 'Dnevno korišćenje funkcija',
  'v1.eng.dailySub': 'Događaji po funkciji i danu',
  'v1.eng.journeys': 'Glavne putanje',
  'v1.eng.journeysText': 'Uređaji koji su u periodu stigli do svakog koraka, kumulativno.',

  'v1.ret.title': 'Zadržavanje',
  'v1.ret.description': 'Stope povratka po kohortama od prve instalacije, bez otkrivanja aktivnosti pojedinačnih korisnika.',
  'v1.ret.matured': '{count} uređaja u sazrelim kohortama',
  'v1.ret.d1': 'Zadržavanje D1',
  'v1.ret.d7': 'Zadržavanje D7',
  'v1.ret.d30': 'Zadržavanje D30',
  'v1.ret.dayTitle': 'Udeo instalacija koje su i dalje aktivne {day}. dana posle instalacije',
  'v1.ret.cohorts': 'Kohorte zadržavanja',
  'v1.ret.cohortsText': 'Udeo svake kohorte instalacija koji je bio aktivan naznačenog dana. Tamnije ćelije znače veće zadržavanje.',
  'v1.ret.cohort': 'Kohorta',
  'v1.ret.size': 'Veličina',
  'v1.ret.status': 'Stanje',
  'v1.ret.uninstallSignals': 'Signali deinstalacije',
  'v1.ret.medianUninstall': 'Medijana {hours} h posle instalacije',
  'v1.ret.footer': 'Crtica znači da kohorta još nije sazrela; ne tumači se kao 0%.',
  'v1.ret.activity': 'Aktivnost',
  'v1.ret.activityText': 'Ko se vraća, koliko često i ko se utišao.',
  'v1.ret.activeDevices': 'Aktivni uređaji',
  'v1.ret.newDevices': 'Novi uređaji',
  'v1.ret.newDevicesHelp': 'Prvi put otvoreno u ovom periodu',
  'v1.ret.returningDevices': 'Uređaji koji se vraćaju',
  'v1.ret.returningDevicesHelp': 'Aktivni, instalirani pre perioda',
  'v1.ret.stickiness': 'Privrženost',
  'v1.ret.stickinessHelp': 'Prosečni dnevno aktivni kao udeo u MAU',
  'v1.ret.frequency': 'Učestalost angažovanja',
  'v1.ret.frequencySub': 'Uređaji po broju aktivnih dana u periodu',
  'v1.ret.recency': 'Skorašnjost',
  'v1.ret.recencySub': 'Uređaji po broju dana od poslednje aktivnosti',
  'v1.ret.dailyActive': 'Dnevno aktivni uređaji',
  'v1.ret.quiet': 'Utihnuli',
  'v1.ret.quietSub': 'Uređaji aktivni u prethodnih 30 dana, ali ne i u poslednjih 7',
  'v1.ret.uninstallDevices': 'Uređaji sa signalom deinstalacije',
  'v1.ret.uninstallInstalls': 'Instalacije u ovim kohortama',
  'v1.ret.signalRate': 'Stopa signala',
  'v1.ret.notMatured': '{label} još nije sazrelo',
  'v1.ret.notMaturedTitle': 'Još nije sazrelo',
  'v1.ret.retainedOf': '{retained} od {size} uređaja',
  'v1.ret.maturedBadge': 'Sazrela',
  'v1.ret.maturing': 'Sazreva',

  'v1.rel.title': 'Pouzdanost',
  'v1.rel.description': 'Stope rada bez pada, padovi, vreme pokretanja i kašnjenje iz Firebase-a i bekenda. Za izvoz koji nedostaje navodi se razlog.',
  'v1.rel.exports': 'Firebase izvozi',
  'v1.rel.ready': 'Spremno',
  'v1.rel.readySince': 'Spremno, od {date}',
  'v1.rel.crashFreeUsers': 'Korisnici bez pada aplikacije',
  'v1.rel.crashFreeSessions': 'Sesije bez pada aplikacije',
  'v1.rel.crashCount': 'Broj padova',
  'v1.rel.nonFatal': 'Greške bez pada',
  'v1.rel.sessions': 'Sesije',
  'v1.rel.crashedSessions': 'Sesije sa padom',
  'v1.rel.startP50': 'Pokretanje aplikacije p50',
  'v1.rel.startP95': 'Pokretanje aplikacije p95',
  'v1.rel.receiptFailure': 'Stopa neuspeha računa',
  'v1.rel.searchFailure': 'Stopa neuspeha pretrage cena',
  'v1.rel.receiptAvg': 'Prosečno kašnjenje računa',
  'v1.rel.receiptP95': 'Kašnjenje računa p95',
  'v1.rel.searchAvg': 'Prosečno kašnjenje pretrage cena',
  'v1.rel.searchP95': 'Kašnjenje pretrage cena p95',
  'v1.rel.errors': 'Greške kroz vreme',
  'v1.rel.p95': 'Kašnjenje p95',
  'v1.rel.traces': 'Firebase Performance merenja',
  'v1.rel.tracesText': 'Prilagođena merenja iz Analitike v1; nedostupni izvozi ostaju N/D.',
  'v1.rel.trace': 'Merenje',
  'v1.rel.count': 'Broj',
  'v1.rel.byVersion': 'Padovi po verziji aplikacije',
  'v1.rel.byVersionShort': 'Padovi po verziji',
  'v1.rel.byVersionText': 'Fatalni padovi i ANR (aplikacija ne odgovara). Sesije dolaze iz Firebase Sessions izvoza.',
  'v1.rel.version': 'Verzija',
  'v1.rel.build': 'Build',
  'v1.rel.crashes': 'Padovi',
  'v1.rel.noCrashes': 'U ovom periodu nema zabeleženih padova.',
  'v1.rel.topIssues': 'Najčešći uzroci padova',
  'v1.rel.topIssuesText': 'Grupisano po Crashlytics problemu; samo brojevi, bez podataka o korisnicima.',
  'v1.rel.issue': 'Problem',
  'v1.rel.type': 'Vrsta',

  'v1.diag.title': 'Dijagnostika',
  'v1.diag.description': 'Privremena provera puštanja. Poziva svih devet analitičkih krajnjih tačaka na reviziji koja se proverava, sa tvojom prijavljenom sesijom. Prikazuje samo statuse i spremnost izvora, nikad akreditive ili podatke.',
  'v1.diag.target': 'Cilj',
  'v1.diag.running': 'Provera u toku...',
  'v1.diag.run': 'Pokreni proveru',
  'v1.diag.copied': 'Kopirano',
  'v1.diag.copy': 'Kopiraj sažetak',
  'v1.diag.pass': 'Kapija puštanja: prolaz',
  'v1.diag.fail': 'Kapija puštanja: pad',
  'v1.diag.passText': 'Svih devet krajnjih tačaka je odgovorilo sa 200 i podacima, a svaki očekivani izvor je bio spreman.',
  'v1.diag.endpoints': 'Krajnje tačke',
  'v1.diag.rangeText': '{start} do {end}',
  'v1.diag.endpoint': 'Krajnja tačka',
  'v1.diag.http': 'HTTP',
  'v1.diag.time': 'Vreme',
  'v1.diag.completeness': 'Potpunost',
  'v1.diag.data': 'Podaci',
  'v1.diag.sources': 'Izvori',
  'v1.diag.failed': 'neuspešno',
  'v1.diag.notReady': 'Izvori koji nisu spremni',
  'v1.diag.source': 'Izvor',
  'v1.diag.status': 'Stanje',
  'v1.diag.reason': 'Razlog',
  'v1.diag.seenOn': 'Viđeno na',

  'v1.export.button': 'Izvoz',
  'v1.export.preparing': 'Priprema…',
  'v1.export.csvCurrent': 'Trenutni odeljak',
  'v1.export.pdfCurrent': 'PDF trenutnog prikaza',
  'v1.export.pdfCurrentSub': 'Izveštaj pogodan za štampu',
  'v1.export.pdfFull': 'Ceo PDF izveštaj',
  'v1.export.pdfFullSub': 'Svih {count} odeljaka',
  'v1.export.failed': 'Izvoz nije uspeo.',
  'v1.section.overview': 'Pregled',
  'v1.section.acquisition': 'Pridobijanje',
  'v1.section.activation': 'Aktivacija',
  'v1.section.retention': 'Zadržavanje',
  'v1.section.features': 'Angažovanje',
  'v1.section.receipts': 'Računi',
  'v1.section.price-comparison': 'Poređenje cena',
  'v1.section.costs': 'Troškovi',
  'v1.section.reliability': 'Pouzdanost',
  'v1.report.title': 'Analitički izveštaj',
  'v1.report.generated': 'Napravljeno',
  'v1.report.source': 'Izvor',
  'v1.report.coverage': 'Pokrivenost: {source}',
  'v1.report.notCollected': 'N/D · nije prikupljeno',
  'v1.report.from': 'od {date}',
  'v1.report.startUnknown': 'početak nepoznat',
  'v1.report.completeThrough': 'potpuno zaključno sa {date}',
  'v1.report.completeUnknown': 'kraj potpunih podataka nepoznat',
  'v1.report.empty': 'Za ovaj period nema dostupnih zbirnih podataka.',
  'v1.report.estimatedUnavailable': 'PROCENA · NIJE DOSTUPNO',
  'v1.report.unavailable': 'NIJE DOSTUPNO',
  'v1.report.kind.actual': 'STVARNO',
  'v1.report.kind.estimated': 'PROCENA',
  'v1.report.kind.mock': 'PROBNO',
  'v1.report.fullCover': 'Ceo zbirni izveštaj · {start} - {end}',
  'v1.report.footer': 'Samo zbirni podaci · Bez ličnih podataka i podataka o pojedincima',
  'v1.report.mockSource': 'PROBNI PODACI - NIJE PRODUKCIJA',
  'v1.report.actualEstimated': 'STVARNO + PROCENA',
  'v1.report.costProvenance': 'Poreklo merenja je navedeno u metapodacima izvora',
  'v1.report.installsByVersion': 'Instalacije po verziji aplikacije',
  'v1.report.installFunnel': 'Levak instalacija',
  'v1.report.step': 'Korak',
  'v1.report.devices': 'Uređaji',
  'v1.report.fromPrevious': 'Od prethodnog',
  'v1.report.fromStart': 'Od početka',
  'v1.report.dropOff': 'Odustajanje',
  'v1.report.activeShare': 'Udeo aktivnih',
  'v1.report.estimatedWarning': 'Troškovi označeni kao PROCENA su modelovane vrednosti, a ne račun.',
  'v1.report.dailyCost': 'Dnevni trošak',
  'v1.report.byService': 'Trošak po servisu',
  'v1.report.service': 'Servis',
  'v1.report.cost': 'Trošak',
  'v1.report.measurement': 'Merenje',
  'v1.report.imagePerDay': 'Računi sa fotografije po danu',
  'v1.report.imageOutcomes': 'Ishodi obrade fotografija',
  'v1.report.retailerDistribution': 'Raspodela po trgovcima',
  'v1.report.processingLatency': 'Kašnjenje obrade',
  'v1.report.retailers': 'Trgovci',
  'v1.report.retailer': 'Trgovac',
  'v1.report.receipts': 'Računi',
  'v1.report.searchesPerDay': 'Pretrage po danu',
  'v1.report.searchOutcomes': 'Ishodi pretrage',
  'v1.report.latency': 'Kašnjenje',
  'v1.csv.report': 'Izveštaj',
  'v1.csv.section': 'Odeljak',
  'v1.csv.dateRange': 'Period',
  'v1.csv.rangeValue': '{start} do {end}',
  'v1.csv.timezone': 'Vremenska zona',
  'v1.csv.generatedAt': 'Napravljeno',
  'v1.csv.dataSource': 'Izvor podataka',
  'v1.csv.sourceCoverage': 'Pokrivenost izvora',
  'v1.csv.privacy': 'Privatnost',
  'v1.csv.privacyValue': 'Samo zbirni podaci',
  'v1.csv.notCollected': 'N/D - nije prikupljeno',
  'v1.csv.kpi': 'KPI',
  'v1.csv.trend': 'Trend',
  'v1.csv.status': 'Stanje',
  'v1.csv.dataset': 'Skup podataka',
  'v1.csv.featureAdoption': 'Korišćenje funkcija',
  'v1.csv.uniqueUsers': 'Jedinstveni korisnici',
  'v1.csv.breakdown': 'Raspodela',
  'v1.csv.breakdownInstalls': '{dimension}: nove instalacije',
  'v1.csv.breakdownActive': '{dimension}: aktivni uređaji',
  'v1.csv.exclusionRule': 'Pravilo izostavljanja',
  'v1.csv.installFunnel': 'Levak instalacija, {segment}',
  'v1.csv.devices': 'Uređaji',
  'v1.csv.users': 'Korisnici',
  'v1.csv.eligible': '{window} obuhvaćeno',
  'v1.csv.rate': '{window} stopa',
  'v1.csv.registrationFunnel': 'Levak registracije',
  'v1.csv.conversionPrevious': 'Konverzija od prethodnog',
  'v1.csv.conversionStart': 'Konverzija od početka',
  'v1.csv.dropOffPrevious': 'Odustajanje od prethodnog',
  'v1.csv.cohort': 'Kohorta',
  'v1.csv.cohortSize': 'Veličina kohorte',
  'v1.csv.periodRetention': 'Zadržavanje {period}',
  'v1.csv.activity': 'Aktivnost',
  'v1.csv.uninstallSignals': 'Signali deinstalacije',
  'v1.csv.feature': 'Funkcija',
  'v1.csv.activeShare': 'Udeo aktivnih korisnika',
  'v1.csv.eventCount': 'Broj događaja',
  'v1.csv.eventsPerUser': 'Događaja po korisniku',
  'v1.csv.repeatUsers': 'Povratni korisnici',
  'v1.csv.repeatRate': 'Stopa ponovnog korišćenja',
  'v1.csv.retailerDistribution': 'Raspodela po trgovcima',
  'v1.csv.receipts': 'Računi',
  'v1.csv.share': 'Udeo',
  'v1.csv.costByService': 'Trošak po servisu',
  'v1.csv.cost': 'Trošak',
  'v1.csv.performanceTrace': 'Performance merenje',
  'v1.csv.traceCount': '{trace} broj',
  'v1.csv.traceP50': '{trace} p50',
  'v1.csv.traceP95': '{trace} p95',

  'v1.label.activeFromNewInstalls': 'Aktivni uređaji instalirani u periodu',
  'v1.label.activeFromEarlierInstalls': 'Aktivni uređaji instalirani ranije',
  'v1.label.rawUninstalls': 'Signali deinstalacije (svi uređaji)',
  'v1.label.uninstallsAny': 'Signali deinstalacije (bilo koji datum instalacije)',
  'v1.label.uninstallsThese': 'Signali deinstalacije (ove instalacije)',
  'v1.label.processed': 'Obrađeni računi sa fotografije',
  'v1.label.successful': 'Uspešna obrada fotografija',
  'v1.label.failed': 'Neuspešna obrada fotografija',
  'v1.label.duplicates': 'Duplikati',
  'v1.label.successRate': 'Stopa uspeha obrade fotografija',
  'v1.label.uniqueReceiptUsers': 'Jedinstveni korisnici računa',
  'v1.label.uniqueUsers': 'Jedinstveni korisnici',
  'v1.label.receiptsPerUser': 'Računa po korisniku',
  'v1.label.receiptsPerUserShort': 'Računa / korisnik',
  'v1.label.avgItems': 'Prosečno stavki po računu',
  'v1.label.itemsPerReceipt': 'Stavki / račun',
  'v1.label.avgProcessing': 'Prosečno trajanje obrade',
  'v1.label.avgProcessingShort': 'Prosečna obrada',
  'v1.label.processingP50': 'Kašnjenje obrade p50',
  'v1.label.processingP95': 'Kašnjenje obrade p95',
  'v1.label.latencyP50': 'Kašnjenje p50',
  'v1.label.latencyP95': 'Kašnjenje p95',
  'v1.label.cacheHitRate': 'Stopa pogodaka keša',
  'v1.label.enrichment': 'Operacije obogaćivanja',
  'v1.label.totalSearches': 'Ukupno pretraga',
  'v1.label.searchesPerUser': 'Pretraga po korisniku',
  'v1.label.searchesPerUserShort': 'Pretraga / korisnik',
  'v1.label.successfulSearches': 'Uspešne pretrage',
  'v1.label.successfulShort': 'Uspešne',
  'v1.label.noResultSearches': 'Pretrage bez rezultata',
  'v1.label.noResultShort': 'Bez rezultata',
  'v1.label.failures': 'Neuspesi',
  'v1.label.noResultRate': 'Stopa pretraga bez rezultata',
  'v1.label.avgResults': 'Prosečno vraćenih rezultata',
  'v1.label.resultsReturned': 'Vraćeni rezultati',
  'v1.label.avgChainCoverage': 'Prosečna najveća pokrivenost lancima',
  'v1.label.maxChainCoverage': 'Najveća pokrivenost lancima',
  'v1.label.avgLatency': 'Prosečno kašnjenje',
  'v1.label.browseRequests': 'Zahtevi za pregled kataloga',
  'v1.label.browsePages': 'Učitane strane pregleda',
  'v1.label.pagesLoaded': 'Učitane strane',
  'v1.label.avgProductsPerBrowse': 'Prosečno proizvoda po pregledu',
  'v1.label.productsPerBrowse': 'Proizvoda / pregled',
  'v1.label.loadMore': 'Korišćenje „Učitaj još”',
  'v1.label.totalCost': 'Ukupan GCP trošak',
  'v1.label.costPerDay': 'Prosečan trošak po danu',
  'v1.label.costPerDayShort': 'Trošak / dan',
  'v1.label.aiCost': 'AI trošak',
  'v1.label.costPerActiveUser': 'Trošak po aktivnom korisniku',
  'v1.label.costPerActiveUserShort': 'Trošak / aktivni korisnik',
  'v1.label.costPerReceipt': 'Trošak po računu sa fotografije',
  'v1.label.costPerReceiptShort': 'Trošak / račun sa fotografije',
  'v1.label.costPerSearch': 'Trošak po pretrazi cena',
  'v1.label.costPerSearchShort': 'Trošak / pretraga',
  'v1.label.aiCallsPerReceipt': 'AI poziva po računu sa fotografije',
  'v1.label.aiCallsPerReceiptShort': 'AI poziva / račun sa fotografije',
  'v1.label.aiCalls': 'AI pozivi',
  'v1.label.aiCostPerCall': 'AI trošak po pozivu',
  'v1.label.receiptAvgLatency': 'Prosečno kašnjenje računa',
  'v1.label.receiptLatency': 'Kašnjenje računa',
  'v1.label.receiptP95': 'Račun p95',
  'v1.label.searchAvgLatency': 'Prosečno kašnjenje pretrage cena',
  'v1.label.searchLatency': 'Kašnjenje pretrage',
  'v1.label.searchP95': 'Pretraga p95',
  'v1.label.searchFailure': 'Stopa neuspeha pretrage',
  'v1.label.crashedUsers': 'Korisnici sa padom aplikacije',
};
